<script setup lang="ts">
/**
 * FeaturesView — 1:1 порт docs/mockups/public-features.html (inbox-162).
 *
 * Спека: docs/specs/2026-05-19-public-behavior.md §27 «Возможности».
 * STYLE_NOTES: hero 88px / 56px title / gradient text / hero-badge.
 *
 * Секции:
 *  1. Hero с badge «6 ключевых возможностей · Один продукт» + jumplinks
 *  2. Smart Routing — 2-col grid (текст + visual с правилами)
 *  3. Аналитика & ClickHouse — 2-col reverse (visual = KPI + chart)
 *  4. Антифрод ML — 2-col grid (visual = fraud rows)
 *  5. QR + Deep Links — 2-col reverse (visual = QR + platform badges)
 *  6. Свои домены — 2-col grid (visual = DNS table)
 *  7. Bio-страницы — 2-col reverse (visual = phone mockup)
 *  8. Feedback CTA banner («Не нашли нужную возможность?»)
 *
 * AppPublic уже даёт sticky nav + footer, поэтому здесь только content.
 */

import { ref } from 'vue';
import AppPublic from '@/layouts/AppPublic.vue';
import { usePageHead, productJsonLd } from '@/composables/usePageHead';

usePageHead({
  title: 'Возможности — Smart routing, аналитика, QR, AI-копайлот',
  description:
    'Российская платформа для сокращения и аналитики ссылок. Smart routing, антифрод, QR-коды, AI-копайлот, интеграции с Яндекс, VK, Bitrix24.',
  path: '/features',
  type: 'website',
  jsonLd: productJsonLd(
    'Возможности — link-platform',
    'Smart routing, аналитика, антифрод, QR, домены и bio-страницы в одной платформе.',
  ),
});

const feedbackText = ref('');

interface JumpLink {
  href: string;
  label: string;
}
const JUMP_LINKS: JumpLink[] = [
  { href: '#routing', label: 'Smart Routing' },
  { href: '#analytics', label: 'Аналитика' },
  { href: '#antifraud', label: 'Антифрод' },
  { href: '#qr', label: 'QR + Deep Links' },
  { href: '#domains', label: 'Свои домены' },
  { href: '#bio', label: 'Bio-страницы' },
];

interface FraudRow {
  level: 'high' | 'med' | 'low';
  ip: string;
  reason: string;
  score: string;
}
const FRAUD_ROWS: FraudRow[] = [
  { level: 'high', ip: '185.220.101.0/24 — Tor', reason: '8 470 кликов / мин', score: '0.97' },
  { level: 'high', ip: 'UA: bot-farm/v2.1', reason: 'Identical fingerprint', score: '0.94' },
  { level: 'med', ip: 'AS12345 (DC)', reason: 'Гео-несоответствие', score: '0.62' },
  { level: 'low', ip: '37.144.0.0/16 — MTS Mobile', reason: 'Нормальный паттерн', score: '0.08' },
];
</script>

<template>
  <AppPublic>
    <!-- HERO -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">6 ключевых возможностей · Один продукт</div>
          <h1 class="hero-title">
            Всё что нужно маркетологу<br>
            для <span class="gradient">работы со ссылками</span>
          </h1>
          <p class="hero-sub">
            Сокращение, разметка, аналитика и автоматизация в одной платформе.
            Никаких пяти разных подписок — всё под одним аккаунтом, на серверах в России.
          </p>
          <div class="feature-jumplinks">
            <a
              v-for="j in JUMP_LINKS"
              :key="j.href"
              :href="j.href"
            >{{ j.label }}</a>
          </div>
        </div>
      </div>
    </section>

    <!-- 1. Smart Routing -->
    <section id="routing" class="feature-section">
      <div class="container">
        <div class="feature-grid">
          <div>
            <span class="feature-eyebrow">Умная маршрутизация</span>
            <h3>Одна ссылка — разные landing'и по контексту</h3>
            <p class="lead">
              Открывайте разные страницы пользователю в зависимости от страны,
              устройства, времени или источника. Никаких UTM-параметров в URL —
              всё решается на сервере за миллисекунды.
            </p>
            <ul class="feature-bullets">
              <li>6 типов условий — гео, устройство, время, реферер, язык, A/B-вариант</li>
              <li>Приоритеты правил — срабатывает первое подходящее</li>
              <li>Fallback на default URL если ни одно не сработало</li>
              <li>A/B-тесты — автоматическое распределение трафика и выбор победителя</li>
              <li>До 10 правил на одну короткую ссылку</li>
            </ul>
          </div>
          <div class="feature-visual">
            <div class="vis-routing-row">
              <span class="num">1</span>
              <span class="cond">Если страна</span>
              <span class="cond">= RU, BY, KZ</span>
              <span class="target">example.ru/landing-ru</span>
            </div>
            <div class="vis-routing-row">
              <span class="num">2</span>
              <span class="cond">Если устройство</span>
              <span class="cond">= mobile</span>
              <span class="target">m.example.com</span>
            </div>
            <div class="vis-routing-row">
              <span class="num">3</span>
              <span class="cond">Если время</span>
              <span class="cond">22:00 — 08:00</span>
              <span class="target">/preview-page</span>
            </div>
            <div class="vis-fallback">
              <b>Fallback:</b> example.com/landing-v3
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. Analytics -->
    <section id="analytics" class="feature-section alt">
      <div class="container">
        <div class="feature-grid reverse">
          <div>
            <span class="feature-eyebrow">Глубокая аналитика</span>
            <h3>Не просто счётчик кликов — поведение, гео, конверсии</h3>
            <p class="lead">
              ClickHouse под капотом обрабатывает миллионы кликов в секунду.
              Дашборды отдаются за 2 секунды, даже если у вас 5 000 кампаний.
            </p>
            <ul class="feature-bullets">
              <li>Гео до уровня района — IP-данные обновляются ежедневно</li>
              <li>Устройство и ОС — модель телефона, разрешение, браузер</li>
              <li>Когорты и retention — поведение в неделю N+1, N+2…</li>
              <li>Воронки конверсий — клик → визит → событие → покупка</li>
              <li>Экспорт в CSV / Excel / Google Sheets / по API</li>
            </ul>
          </div>
          <div class="feature-visual">
            <div class="vis-chart-kpi">
              <div class="vis-kpi-card">
                <div class="label">Клики</div>
                <div class="value">847К</div>
                <div class="delta">+23%</div>
              </div>
              <div class="vis-kpi-card">
                <div class="label">Уникальные</div>
                <div class="value">312К</div>
                <div class="delta">+18%</div>
              </div>
              <div class="vis-kpi-card">
                <div class="label">CR</div>
                <div class="value">4.8%</div>
                <div class="delta">+0.7пп</div>
              </div>
            </div>
            <div class="vis-chart">
              <svg viewBox="0 0 400 100" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="cg-features-chart" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stop-color="#4f8cff" stop-opacity="0.4" />
                    <stop offset="100%" stop-color="#4f8cff" stop-opacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,85 L40,75 L80,65 L120,55 L160,68 L200,48 L240,35 L280,42 L320,25 L360,30 L400,15 L400,100 L0,100 Z"
                  fill="url(#cg-features-chart)"
                />
                <path
                  d="M0,85 L40,75 L80,65 L120,55 L160,68 L200,48 L240,35 L280,42 L320,25 L360,30 L400,15"
                  fill="none"
                  stroke="#4f8cff"
                  stroke-width="2"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. Antifraud -->
    <section id="antifraud" class="feature-section">
      <div class="container">
        <div class="feature-grid">
          <div>
            <span class="feature-eyebrow">Антифрод ML</span>
            <h3>Фильтрует ботов до того, как они портят отчёты</h3>
            <p class="lead">
              Собственная нейросеть детектирует бот-фермы, накрутки и
              аномальные паттерны. Каждый клик получает fraud-score от 0.0 до 1.0 —
              порог настраиваемый.
            </p>
            <ul class="feature-bullets">
              <li>Детект click-farm трафика по поведенческим сигналам</li>
              <li>Аномалии User-Agent, fingerprint reuse, гео-несоответствие</li>
              <li>Время между кликами — бот пройдёт 100 ссылок за 10 секунд</li>
              <li>Подозрительный трафик помечается, но не блокируется автоматом</li>
              <li>Отчёт по источникам фрода (IP-диапазоны, ASN, UA-pattern)</li>
            </ul>
          </div>
          <div class="feature-visual">
            <div
              v-for="r in FRAUD_ROWS"
              :key="r.ip"
              :class="['vis-fraud-row', r.level]"
            >
              <span class="dot" />
              <span class="ip">{{ r.ip }}</span>
              <span class="reason">{{ r.reason }}</span>
              <span class="score">{{ r.score }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 4. QR + Deep Links -->
    <section id="qr" class="feature-section alt">
      <div class="container">
        <div class="feature-grid reverse">
          <div>
            <span class="feature-eyebrow">QR-коды и Deep Links</span>
            <h3>Из офлайн-материалов прямо в приложение</h3>
            <p class="lead">
              Динамические QR — один напечатанный код, разные таргеты в разное время.
              iOS Universal Links и Android App Links открывают вашу ссылку
              в нативном приложении, а не в браузере.
            </p>
            <ul class="feature-bullets">
              <li>Кастомизация QR — цвета, форма, лого, рамка</li>
              <li>Print-ready экспорт: PNG / SVG / EPS / PDF в 300 DPI</li>
              <li>iOS Universal Links — apple-app-site-association генерируется автоматически</li>
              <li>Android App Links — assetlinks.json тоже</li>
              <li>Fallback на web если приложение не установлено</li>
            </ul>
          </div>
          <div class="feature-visual">
            <div class="vis-qr-row">
              <div class="vis-qr" aria-hidden="true" />
              <div class="vis-qr-info">
                <div class="vis-qr-title">go.brand.ru/promo</div>
                <div class="vis-qr-sub">3 412 сканирований за неделю</div>
                <div class="platform-badges">
                  <span>iOS</span>
                  <span>Android</span>
                  <span>Web</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 5. Custom domains -->
    <section id="domains" class="feature-section">
      <div class="container">
        <div class="feature-grid">
          <div>
            <span class="feature-eyebrow">Свои домены</span>
            <h3>Брендированные ссылки на go.brand.ru</h3>
            <p class="lead">
              Подключаем ваш домен за 5 минут. Получите автоматический SSL
              от Let's Encrypt, не настраивая серверы. Поддомены тоже работают.
            </p>
            <ul class="feature-bullets">
              <li>Один CNAME-запись — и ссылки начинают работать через ваш домен</li>
              <li>SSL автоматический, обновляется без участия</li>
              <li>Несколько доменов на одном тарифе — по тарифу 1 — 5 — без лимита</li>
              <li>Wildcard поддерживается — *.brand.ru для разных продуктов</li>
              <li>DNS-чекер диагностирует проблемы автоматически</li>
            </ul>
          </div>
          <div class="feature-visual">
            <div class="vis-dns-table">
              <div class="head" style="grid-column: 1 / -1;">DNS-записи для go.brand.ru</div>
              <div class="cell fg">TYPE</div>
              <div class="cell fg">HOST</div>
              <div class="cell fg">VALUE</div>
              <div class="cell">CNAME</div>
              <div class="cell fg">go</div>
              <div class="cell">cname.link-platform.ru</div>
              <div class="cell">TXT</div>
              <div class="cell fg">_verify.go</div>
              <div class="cell">lp-verify-a8c4f…</div>
            </div>
            <div class="vis-dns-status">
              ✓ Подтверждён · SSL выпущен до 12 марта 2027
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 6. Bio-pages -->
    <section id="bio" class="feature-section alt">
      <div class="container">
        <div class="feature-grid reverse">
          <div>
            <span class="feature-eyebrow">Bio-страницы</span>
            <h3>Витрина из ссылок для соцсетей</h3>
            <p class="lead">
              Одна страница со всеми вашими активностями для Instagram / TikTok / VK.
              Конструктор без кода, виджеты, темы, кастомные шрифты.
            </p>
            <ul class="feature-bullets">
              <li>Виджеты — ссылки, формы подписки, видео, продукт-карточки, соцсети</li>
              <li>Drag-drop редактор с iPhone-preview справа</li>
              <li>Темы и кастомные цвета — соответствие вашему бренду</li>
              <li>Heatmap кликов — какие виджеты работают, какие — нет</li>
              <li>Сбор email/телефон через форму (с согласием 152-ФЗ)</li>
            </ul>
          </div>
          <div class="feature-visual feature-visual-plain">
            <div class="vis-phone">
              <div class="vis-phone-head">
                <div class="vis-phone-avatar">АА</div>
                <div class="vis-phone-name">@alfa.brand</div>
                <div class="vis-phone-desc">Маркетплейс одежды</div>
              </div>
              <div class="vis-phone-widget">
                <span class="ico">→</span> Новая коллекция Лето-2026
              </div>
              <div class="vis-phone-widget">
                <span class="ico">▶</span> Видео с показа
              </div>
              <div class="vis-phone-widget">
                <span class="ico">✉</span> Подписаться на рассылку
              </div>
              <div class="vis-phone-widget">
                <span class="ico">VK</span> Группа ВКонтакте
              </div>
              <div class="vis-phone-widget">
                <span class="ico">TG</span> Канал в Telegram
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Feedback CTA -->
    <section>
      <div class="container">
        <div class="feedback-banner">
          <h3>Не нашли нужную возможность?</h3>
          <p>
            Расскажите, что не хватает. Мы читаем каждое сообщение и регулярно
            добавляем фичи по запросу клиентов — обычно в течение 1-2 спринтов.
          </p>
          <form
            class="input-row"
            @submit.prevent
          >
            <input
              v-model="feedbackText"
              type="text"
              placeholder="Опишите кейс или нужную фичу…"
              aria-label="Опишите кейс или нужную фичу"
            >
            <button
              type="submit"
              class="feedback-submit"
            >
              Отправить
            </button>
          </form>
        </div>
      </div>
    </section>
  </AppPublic>
</template>

<style scoped>
.container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 32px;
}

/* HERO */
.hero {
  position: relative;
  padding: 88px 0 64px;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 30% 20%, rgba(79, 140, 255, 0.15), transparent 70%),
    radial-gradient(ellipse 50% 40% at 80% 30%, rgba(124, 92, 255, 0.10), transparent 70%);
  pointer-events: none;
}
.hero-content {
  position: relative;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  margin-bottom: 24px;
  background: rgba(79, 140, 255, 0.12);
  border: 1px solid rgba(79, 140, 255, 0.25);
  border-radius: 100px;
  font-size: 13px;
  color: #6ea1ff;
  font-weight: 500;
}
.hero-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4f8cff;
  box-shadow: 0 0 8px #4f8cff;
}
h1.hero-title {
  font-size: 56px;
  line-height: 1.08;
  letter-spacing: -0.025em;
  font-weight: 700;
  margin-bottom: 24px;
  color: #e8edf3;
}
h1.hero-title .gradient {
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.hero-sub {
  font-size: 19px;
  line-height: 1.55;
  color: #8b95a5;
  max-width: 640px;
  margin: 0 auto 32px;
}
.feature-jumplinks {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}
.feature-jumplinks a {
  padding: 7px 14px;
  font-size: 13.5px;
  background: #161c26;
  border: 1px solid #1f2733;
  border-radius: 100px;
  color: #8b95a5;
  transition: all 0.15s;
  text-decoration: none;
}
.feature-jumplinks a:hover {
  border-color: #4f8cff;
  color: #6ea1ff;
}

/* Feature deep-dive sections */
.feature-section {
  padding: 80px 0;
  border-top: 1px solid #1f2733;
}
.feature-section.alt {
  background: #11161e;
}
.feature-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}
.feature-grid.reverse > div:first-child {
  order: 2;
}
.feature-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  margin-bottom: 16px;
  background: rgba(79, 140, 255, 0.12);
  border: 1px solid rgba(79, 140, 255, 0.25);
  border-radius: 100px;
  font-size: 12px;
  color: #6ea1ff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.feature-section h3 {
  font-size: 32px;
  line-height: 1.18;
  letter-spacing: -0.02em;
  font-weight: 700;
  margin-bottom: 14px;
  color: #e8edf3;
}
.feature-section p.lead {
  font-size: 16.5px;
  color: #8b95a5;
  line-height: 1.6;
  margin-bottom: 22px;
}
.feature-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
}
.feature-bullets li {
  font-size: 14.5px;
  color: #e8edf3;
  line-height: 1.55;
  padding: 9px 0 9px 28px;
  position: relative;
}
.feature-bullets li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 14px;
  width: 14px;
  height: 8px;
  border-left: 1.5px solid #4f8cff;
  border-bottom: 1.5px solid #4f8cff;
  transform: rotate(-45deg);
}
.feature-visual {
  background: #161c26;
  border: 1px solid #2a3441;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;
}
.feature-visual::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 60% 80% at 50% 0%,
    rgba(79, 140, 255, 0.06),
    transparent 60%
  );
  pointer-events: none;
}
.feature-visual > * {
  position: relative;
}
.feature-visual-plain {
  background: transparent;
  border: 0;
  box-shadow: none;
  padding: 0;
}
.feature-visual-plain::before {
  display: none;
}

/* Visual: routing rules */
.vis-routing-row {
  display: grid;
  grid-template-columns: 24px 80px 1fr 1fr;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #1f2733;
  font-size: 13px;
}
.vis-routing-row:last-of-type {
  border-bottom: 0;
}
.vis-routing-row .num {
  color: #5a6577;
  font-family: 'SF Mono', Menlo, monospace;
}
.vis-routing-row .cond {
  background: #11161e;
  border: 1px solid #1f2733;
  border-radius: 6px;
  padding: 5px 10px;
  color: #e8edf3;
  font-size: 12.5px;
}
.vis-routing-row .target {
  color: #6ea1ff;
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 12px;
}
.vis-fallback {
  margin-top: 14px;
  padding: 10px 12px;
  background: #11161e;
  border: 1px dashed #2a3441;
  border-radius: 8px;
  font-size: 12.5px;
  color: #8b95a5;
}
.vis-fallback b {
  color: #e8edf3;
}

/* Visual: analytics chart */
.vis-chart-kpi {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.vis-kpi-card {
  background: #11161e;
  border: 1px solid #1f2733;
  border-radius: 8px;
  padding: 10px 12px;
}
.vis-kpi-card .label {
  font-size: 11px;
  color: #5a6577;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 3px;
}
.vis-kpi-card .value {
  font-size: 19px;
  font-weight: 700;
  color: #e8edf3;
}
.vis-kpi-card .delta {
  font-size: 11px;
  color: #3ecf8e;
  margin-top: 2px;
}
.vis-chart {
  height: 120px;
  padding: 12px;
  background: #11161e;
  border: 1px solid #1f2733;
  border-radius: 8px;
}
.vis-chart svg {
  width: 100%;
  height: 100%;
}

/* Visual: antifraud */
.vis-fraud-row {
  display: grid;
  grid-template-columns: 24px 1fr auto auto;
  gap: 10px;
  padding: 9px 0;
  align-items: center;
  border-bottom: 1px solid #1f2733;
  font-size: 13px;
}
.vis-fraud-row:last-child {
  border-bottom: 0;
}
.vis-fraud-row .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin: 0 auto;
}
.vis-fraud-row.high .dot {
  background: #ff6b6b;
}
.vis-fraud-row.med .dot {
  background: #f5a524;
}
.vis-fraud-row.low .dot {
  background: #3ecf8e;
}
.vis-fraud-row .ip {
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 12px;
  color: #e8edf3;
}
.vis-fraud-row .reason {
  color: #8b95a5;
  font-size: 12.5px;
}
.vis-fraud-row .score {
  color: #e8edf3;
  font-weight: 600;
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 12px;
}

/* Visual: QR + Deep links */
.vis-qr-row {
  display: flex;
  gap: 16px;
  align-items: center;
}
.vis-qr {
  width: 110px;
  height: 110px;
  background: #fff;
  border-radius: 8px;
  background-image:
    linear-gradient(45deg, #000 25%, transparent 25%),
    linear-gradient(-45deg, #000 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #000 75%),
    linear-gradient(-45deg, transparent 75%, #000 75%);
  background-size: 14px 14px;
  background-position: 0 0, 0 7px, 7px -7px, -7px 0;
  position: relative;
  flex-shrink: 0;
}
.vis-qr::after {
  content: 'L';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  color: #fff;
  border-radius: 6px;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 18px;
}
.vis-qr-info {
  flex: 1;
}
.vis-qr-title {
  font-size: 14px;
  font-weight: 600;
  color: #e8edf3;
}
.vis-qr-sub {
  font-size: 12.5px;
  color: #8b95a5;
  margin-top: 4px;
}
.vis-qr-info .platform-badges {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}
.vis-qr-info .platform-badges span {
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 100px;
  background: #11161e;
  border: 1px solid #1f2733;
  color: #8b95a5;
}

/* Visual: custom domain DNS */
.vis-dns-table {
  display: grid;
  grid-template-columns: 60px 1fr 1fr;
  gap: 8px;
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 11.5px;
}
.vis-dns-table .head {
  font-family: inherit;
  font-weight: 600;
  color: #5a6577;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-bottom: 6px;
  border-bottom: 1px solid #1f2733;
}
.vis-dns-table .cell {
  padding: 7px 0;
  color: #8b95a5;
  border-bottom: 1px solid #1f2733;
}
.vis-dns-table .cell:last-child,
.vis-dns-table .cell:nth-last-child(2),
.vis-dns-table .cell:nth-last-child(3) {
  border-bottom: 0;
}
.vis-dns-table .cell.fg {
  color: #e8edf3;
}
.vis-dns-status {
  margin-top: 14px;
  padding: 10px 12px;
  background: rgba(62, 207, 142, 0.08);
  border: 1px solid rgba(62, 207, 142, 0.3);
  border-radius: 8px;
  font-size: 12.5px;
  color: #3ecf8e;
}

/* Visual: bio phone */
.vis-phone {
  margin: 0 auto;
  width: 220px;
  height: 380px;
  background: #000;
  border: 6px solid #1a1f28;
  border-radius: 30px;
  padding: 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
}
.vis-phone-head {
  text-align: center;
  padding: 6px 0;
}
.vis-phone-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  margin: 0 auto 6px;
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 700;
}
.vis-phone-name {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}
.vis-phone-desc {
  font-size: 11px;
  color: #8b95a5;
  margin-top: 2px;
}
.vis-phone-widget {
  background: #161c26;
  border: 1px solid #1f2733;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: #e8edf3;
  display: flex;
  align-items: center;
  gap: 8px;
}
.vis-phone-widget .ico {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  background: rgba(79, 140, 255, 0.12);
  color: #6ea1ff;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
}

/* Feedback CTA */
.feedback-banner {
  background: #161c26;
  border: 1px solid #2a3441;
  border-radius: 16px;
  padding: 40px 48px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.feedback-banner::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 60% 80% at 50% 0%,
    rgba(79, 140, 255, 0.10),
    transparent 60%
  );
  pointer-events: none;
}
.feedback-banner > * {
  position: relative;
}
.feedback-banner h3 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #e8edf3;
}
.feedback-banner p {
  font-size: 15.5px;
  color: #8b95a5;
  margin: 0 auto 22px;
  max-width: 540px;
}
.feedback-banner .input-row {
  display: flex;
  gap: 8px;
  max-width: 480px;
  margin: 0 auto;
}
.feedback-banner input {
  flex: 1;
  background: #11161e;
  border: 1px solid #1f2733;
  color: #e8edf3;
  border-radius: 8px;
  padding: 11px 14px;
  font: inherit;
  font-size: 14px;
  outline: 0;
}
.feedback-banner input:focus {
  border-color: #4f8cff;
}
.feedback-submit {
  white-space: nowrap;
  padding: 11px 18px;
  background: #4f8cff;
  color: #fff;
  border: 1px solid #4f8cff;
  border-radius: 8px;
  font: inherit;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s;
}
.feedback-submit:hover {
  background: #6ea1ff;
  border-color: #6ea1ff;
}

/* RESPONSIVE */
@media (max-width: 980px) {
  .container {
    padding: 0 24px;
  }
  h1.hero-title {
    font-size: 44px;
  }
  .hero-sub {
    font-size: 17px;
  }
  .feature-section h3 {
    font-size: 26px;
  }
  .feature-grid {
    grid-template-columns: 1fr;
    gap: 36px;
  }
  .feature-grid.reverse > div:first-child {
    order: 0;
  }
}
@media (max-width: 640px) {
  .container {
    padding: 0 18px;
  }
  .hero {
    padding: 48px 0 40px;
  }
  h1.hero-title {
    font-size: 30px;
  }
  .hero-sub {
    font-size: 15.5px;
    margin-bottom: 24px;
  }
  .feature-jumplinks a {
    font-size: 12px;
    padding: 6px 11px;
  }
  .feature-section {
    padding: 56px 0;
  }
  .feature-section h3 {
    font-size: 22px;
  }
  .feature-visual {
    padding: 18px;
  }
  .feedback-banner {
    padding: 32px 22px;
  }
  .feedback-banner .input-row {
    flex-direction: column;
    gap: 10px;
  }
  .feedback-submit {
    width: 100%;
  }
}
</style>
